import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL as API_URL } from '../utils/backendUrl';
/**
 * NEXUS V6.3 Phase 2B - Knowledge Pack Manager API Client
 * Handles all 13 Knowledge Pack Manager endpoints
 */

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  return secureStorage.getItem('nexus_access_token');
};

/**
 * Make authenticated API request
 */
const makeRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.detail || error.error || `HTTP ${response.status}`);
  }

  return response.json();
};

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * List all Knowledge Packs with filters
 * @param {Object} params - { type, status, search, limit, skip, user_role }
 */
export const listKnowledgePacks = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append('type', params.type);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  if (params.user_role) queryParams.append('user_role', params.user_role);

  return makeRequest(`/api/knowledge-packs?${queryParams.toString()}`);
};

/**
 * Get single Knowledge Pack by ID
 * @param {string} packId - Pack UUID
 * @param {string} userRole - User role
 */
export const getKnowledgePack = async (packId, userRole) => {
  return makeRequest(`/api/knowledge-packs/${packId}?user_role=${userRole}`);
};

/**
 * Create new Knowledge Pack
 * @param {Object} packData - { name, type, hard_constraints, soft_constraints, tags, metadata }
 * @param {string} userRole - User role
 */
export const createKnowledgePack = async (packData, userRole) => {
  return makeRequest(`/api/knowledge-packs?user_role=${userRole}`, {
    method: 'POST',
    body: JSON.stringify(packData),
  });
};

/**
 * Update existing Knowledge Pack
 * @param {string} packId - Pack UUID
 * @param {Object} updates - { name, hard_constraints, soft_constraints, tags, metadata }
 * @param {string} userRole - User role
 */
export const updateKnowledgePack = async (packId, updates, userRole) => {
  return makeRequest(`/api/knowledge-packs/${packId}?user_role=${userRole}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

/**
 * Delete (Archive) Knowledge Pack
 * @param {string} packId - Pack UUID
 * @param {string} userId - User UUID
 * @param {string} userRole - User role
 */
export const deleteKnowledgePack = async (packId, userId, userRole) => {
  return makeRequest(`/api/knowledge-packs/${packId}?user_id=${userId}&user_role=${userRole}`, {
    method: 'DELETE',
  });
};

/**
 * Publish Knowledge Pack
 * @param {string} packId - Pack UUID
 * @param {Object} data - { published_by, publish_reason }
 * @param {string} userRole - User role
 */
export const publishKnowledgePack = async (packId, data, userRole) => {
  return makeRequest(`/api/knowledge-packs/${packId}/publish?user_role=${userRole}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Rollback Knowledge Pack to previous version
 * @param {string} packId - Pack UUID
 * @param {number} versionNum - Target version number
 * @param {string} rollbackBy - User UUID
 * @param {string} rollbackReason - Reason for rollback
 * @param {string} userRole - User role
 */
export const rollbackKnowledgePack = async (packId, versionNum, rollbackBy, rollbackReason, userRole) => {
  return makeRequest(
    `/api/knowledge-packs/${packId}/versions/${versionNum}/rollback?rollback_by=${rollbackBy}&rollback_reason=${encodeURIComponent(rollbackReason)}&user_role=${userRole}`,
    { method: 'POST' }
  );
};

// ============================================================================
// Versioning
// ============================================================================

/**
 * Get version history for a pack
 * @param {string} packId - Pack UUID
 */
export const getVersionHistory = async (packId) => {
  return makeRequest(`/api/knowledge-packs/${packId}/versions`);
};

/**
 * Create new version of pack
 * @param {string} packId - Pack UUID
 * @param {string} userId - User UUID
 * @param {string} userRole - User role
 */
export const createVersion = async (packId, userId, userRole) => {
  return makeRequest(`/api/knowledge-packs/${packId}/versions?user_id=${userId}&user_role=${userRole}`, {
    method: 'POST',
  });
};

// ============================================================================
// Audit Logs
// ============================================================================

/**
 * Get audit logs for specific pack
 * @param {string} packId - Pack UUID
 * @param {Object} params - { limit, skip }
 */
export const getPackAuditLogs = async (packId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);

  return makeRequest(`/api/knowledge-packs/${packId}/audit?${queryParams.toString()}`);
};

/**
 * Get all audit logs (global)
 * @param {Object} params - { action, pack_id, performed_by, limit, skip, user_role }
 */
export const getAllAuditLogs = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.action) queryParams.append('action', params.action);
  if (params.pack_id) queryParams.append('pack_id', params.pack_id);
  if (params.performed_by) queryParams.append('performed_by', params.performed_by);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.skip) queryParams.append('skip', params.skip);
  if (params.user_role) queryParams.append('user_role', params.user_role);

  return makeRequest(`/api/knowledge-packs/audit?${queryParams.toString()}`);
};

// ============================================================================
// Spider Web Weights
// ============================================================================

/**
 * Get Spider Web weights for user
 * @param {string} userId - User UUID
 * @param {string} projectId - Optional project UUID
 */
export const getSpiderWebWeights = async (userId, projectId = null) => {
  const queryParams = projectId ? `?project_id=${projectId}` : '';
  return makeRequest(`/api/spider-web/weights/${userId}${queryParams}`);
};

/**
 * Update Spider Web weights for user
 * @param {string} userId - User UUID
 * @param {Object} weightsData - Complete weights data object
 */
export const updateSpiderWebWeights = async (userId, weightsData) => {
  return makeRequest(`/api/spider-web/weights/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(weightsData),
  });
};

// ============================================================================
// System Configuration
// ============================================================================

/**
 * Get system configuration
 */
export const getSystemConfig = async () => {
  return makeRequest('/api/system/config');
};

/**
 * Update system configuration (Admin only)
 * @param {Object} config - { pack_types_enabled, compliance_threshold }
 * @param {string} updatedBy - User UUID
 * @param {string} userRole - User role
 */
export const updateSystemConfig = async (config, updatedBy, userRole) => {
  const queryParams = new URLSearchParams();
  if (config.pack_types_enabled) queryParams.append('pack_types_enabled', config.pack_types_enabled.join(','));
  if (config.compliance_threshold) queryParams.append('compliance_threshold', config.compliance_threshold);
  queryParams.append('updated_by', updatedBy);
  queryParams.append('user_role', userRole);

  return makeRequest(`/api/system/config?${queryParams.toString()}`, {
    method: 'PUT',
  });
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Pack types enum
 */
export const PACK_TYPES = [
  'Product',
  'Campaign',
  'Promotion',
  'Ambassador',
  'Trend',
  'Compliance',
  'PlatformRule',
  'BrandTone',
  'TargetAudience',
  'CreativeTemplate',
];

/**
 * Active pack types (Phase 2B - only 3 types in UI)
 */
export const ACTIVE_PACK_TYPES = ['Product', 'Campaign', 'Compliance'];

/**
 * Pack status enum
 */
export const PACK_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
};

/**
 * Audit action types
 */
export const AUDIT_ACTIONS = {
  CREATED: 'created',
  UPDATED: 'updated',
  DELETED: 'deleted',
  VERSION_CREATED: 'version_created',
  VERSION_PUBLISHED: 'version_published',
  VERSION_ROLLED_BACK: 'version_rolled_back',
};

/**
 * Get pack type icon
 */
export const getPackTypeIcon = (type) => {
  const icons = {
    Product: '📦',
    Campaign: '📢',
    Promotion: '🎁',
    Ambassador: '👤',
    Trend: '📈',
    Compliance: '⚖️',
    PlatformRule: '🔧',
    BrandTone: '🎨',
    TargetAudience: '🎯',
    CreativeTemplate: '✨',
  };
  return icons[type] || '📋';
};

/**
 * Get pack status color
 */
export const getPackStatusColor = (status) => {
  const colors = {
    draft: 'text-gray-500 bg-gray-100',
    active: 'text-green-600 bg-green-100',
    archived: 'text-red-500 bg-red-100',
  };
  return colors[status] || 'text-gray-500 bg-gray-100';
};

/**
 * Format date/time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-MY', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Export audit logs to CSV
 */
export const exportAuditLogsToCSV = (logs) => {
  const headers = ['Timestamp', 'Action', 'Performed By', 'Pack ID', 'Changes', 'Reason', 'ISO Hash'];
  const rows = logs.map(log => [
    formatDateTime(log.timestamp),
    log.action,
    log.performed_by,
    log.pack_id,
    JSON.stringify(log.changes),
    log.reason || '',
    log.iso_hash || '',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `knowledge-pack-audit-logs-${new Date().toISOString()}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};
